(() => {
  if (window.moonroadFacetA11yInitialized) return;
  window.moonroadFacetA11yInitialized = true;
  document.documentElement.setAttribute('data-moonroad-retail-js', 'ready');

  let activeTrigger = null;

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const getDrawer = (trigger) => {
    const drawerId = trigger && trigger.getAttribute('aria-controls');
    return drawerId ? document.getElementById(drawerId) : null;
  };

  const getFocusable = (drawer) => Array.from(drawer.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
  )).filter(isVisible);

  const finishClose = () => {
    if (!activeTrigger) return;
    const trigger = activeTrigger;
    const drawer = getDrawer(trigger);
    trigger.setAttribute('aria-expanded', 'false');
    if (drawer) {
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.removeAttribute('aria-label');
    }
    activeTrigger = null;
    window.setTimeout(() => trigger.focus(), 0);
  };

  const startOpen = (trigger) => {
    activeTrigger = trigger;
    window.setTimeout(() => {
      const drawer = getDrawer(trigger);
      if (!drawer) return;
      trigger.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', trigger.textContent.trim());
      const closeButton = Array.from(drawer.querySelectorAll('.js-close-filters')).find(isVisible);
      if (closeButton) closeButton.focus();
    }, 0);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.js-show-filters, .js-show-sort');
    if (trigger) {
      startOpen(trigger);
      return;
    }

    if (event.target.closest('.js-close-filters') && activeTrigger) {
      finishClose();
    }
  });

  document.addEventListener('keydown', (event) => {
    const trigger = event.target.closest('.js-show-filters, .js-show-sort');
    if (trigger && (event.key === 'Enter' || event.key === ' ')) {
      startOpen(trigger);
      return;
    }

    if (!activeTrigger) return;
    const drawer = getDrawer(activeTrigger);
    if (!drawer) return;

    if (event.key === 'Escape') {
      const closeButton = Array.from(drawer.querySelectorAll('.js-close-filters')).find(isVisible);
      if (closeButton) {
        event.preventDefault();
        closeButton.click();
      }
      return;
    }

    if (event.key !== 'Tab' || !drawer.contains(document.activeElement)) return;
    const focusable = getFocusable(drawer);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();

(() => {
  if (window.moonroadHeaderA11yInitialized) return;
  window.moonroadHeaderA11yInitialized = true;

  const mobileMaxWidth = 939.98;
  let menuTrigger = null;
  let activeMenu = null;
  let searchTrigger = null;
  let activeSearch = null;

  const isMobile = () => window.innerWidth <= mobileMaxWidth;

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const setInert = (element, inert) => {
    if (!element) return;
    element.inert = inert;
    if (inert) {
      element.setAttribute('inert', '');
    } else {
      element.removeAttribute('inert');
    }
  };

  const getHeader = (element) => (
    element && element.closest('.site-header[data-section-type="header"]')
  ) || document.querySelector('.site-header[data-section-type="header"]');

  const getFocusable = (container) => Array.from(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
  )).filter(isVisible);

  const findVisible = (container, selector) => (
    Array.from(container.querySelectorAll(selector)).find(isVisible)
  );

  const clearDialog = (element) => {
    if (!element) return;
    element.removeAttribute('role');
    element.removeAttribute('aria-modal');
    element.removeAttribute('aria-label');
  };

  const openMenu = (trigger) => {
    if (!isMobile()) return;
    const menu = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!menu || !document.body.classList.contains('mobile-menu-open')) return;

    menuTrigger = trigger;
    activeMenu = menu;
    setInert(menu, false);
    trigger.setAttribute('aria-expanded', 'true');
    menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-modal', 'true');
    menu.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Menu');

    const closeButton = findVisible(menu, '.js-close-mobile-menu');
    if (closeButton) closeButton.focus();
  };

  const closeMenu = (returnFocus = true) => {
    const trigger = menuTrigger;
    const menu = activeMenu;

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    clearDialog(menu);
    if (menu && isMobile()) setInert(menu, true);

    menuTrigger = null;
    activeMenu = null;

    if (returnFocus && trigger && trigger.isConnected && isVisible(trigger)) {
      window.setTimeout(() => trigger.focus(), 0);
    }
  };

  const findOpenSearch = (header) => Array.from(header.querySelectorAll(
    '.search-bar:not(.mobile-menu-search)'
  )).find((bar) => isVisible(bar) && findVisible(bar, '.js-search-form-close'));

  const openSearch = (trigger) => {
    const header = getHeader(trigger);
    if (!header || !document.body.classList.contains('search-bar-open')) return;

    const search = findOpenSearch(header);
    if (!search) return;

    searchTrigger = trigger;
    activeSearch = search;
    trigger.setAttribute('aria-expanded', 'true');

    if (isMobile()) {
      search.setAttribute('role', 'dialog');
      search.setAttribute('aria-modal', 'true');
      search.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Search');
    }

    const input = findVisible(search, 'input[name="q"]');
    if (input) input.focus();
  };

  const closeSearch = (returnFocus = true) => {
    const trigger = searchTrigger;

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    clearDialog(activeSearch);
    searchTrigger = null;
    activeSearch = null;

    if (returnFocus && trigger && trigger.isConnected && isVisible(trigger)) {
      window.setTimeout(() => trigger.focus(), 0);
    }
  };

  const syncViewport = () => {
    document.querySelectorAll('.site-header[data-section-type="header"]').forEach((header) => {
      const menu = header.querySelector('.navigation__container');
      const trigger = header.querySelector('.js-mobile-menu-icon');
      if (!menu) return;

      if (isMobile()) {
        const isOpen = header.querySelector('.header-navigation--open') && document.body.classList.contains('mobile-menu-open');
        setInert(menu, !isOpen);
      } else {
        setInert(menu, false);
        clearDialog(menu);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    });

    if (!isMobile() && document.body.classList.contains('mobile-menu-open')) {
      document.body.classList.remove('mobile-menu-open');
      document.querySelectorAll('.header-navigation--open').forEach((header) => {
        header.classList.remove('header-navigation--open');
      });
      closeMenu(false);
    }
  };

  document.addEventListener('click', (event) => {
    const searchOpenButton = event.target.closest('.js-search-form-open');
    if (searchOpenButton) {
      searchTrigger = searchOpenButton;
      searchOpenButton.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => openSearch(searchOpenButton), 0);
    }

    if (event.target.closest('.js-search-form-close')) {
      window.setTimeout(() => closeSearch(true), 0);
    }

    const menuOpenButton = event.target.closest('.js-mobile-menu-icon');
    if (menuOpenButton) {
      menuTrigger = menuOpenButton;
      window.setTimeout(() => openMenu(menuOpenButton), 0);
    }

    if (event.target.closest('.js-close-mobile-menu')) {
      window.setTimeout(() => closeMenu(true), 0);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('search-bar-open')) {
      const header = getHeader(searchTrigger);
      const search = activeSearch || (header && findOpenSearch(header));
      const closeButton = search && findVisible(search, '.js-search-form-close');
      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        closeButton.click();
      }
      return;
    }

    if (event.key === 'Escape' && document.body.classList.contains('mobile-menu-open')) {
      const header = getHeader(menuTrigger);
      const menu = activeMenu || (header && header.querySelector('.navigation__container'));
      const closeButton = menu && findVisible(menu, '.js-close-mobile-menu');
      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        closeButton.click();
      }
      return;
    }

    if (event.key !== 'Tab' || !isMobile()) return;
    const trap = (
      document.body.classList.contains('search-bar-open') && activeSearch
    ) || (
      document.body.classList.contains('mobile-menu-open') && activeMenu
    );
    if (!trap || !trap.contains(document.activeElement)) return;

    const focusable = getFocusable(trap);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  window.addEventListener('resize', syncViewport);
  syncViewport();
})();
